<template>
  <div
    class="map-shell"
    :class="{ 'map-shell--point-sheet': pointTool.point.value && !routeEditor.active.value, 'map-shell--tools-covered': toolsCovered }"
    :style="{ '--map-sheet-inset': `${mapSheetInset}px` }"
  >
    <div class="map-container" ref="mapEl" data-map-root></div>
    <!-- Перекрестие в центре карты: от центра считается поиск (store/searchFocus).
         В редакторе маршрута прячем, чтобы не путать с курсором рисования. -->
    <div v-if="!routeEditor.active.value" class="map-crosshair" aria-hidden="true" />
    <!-- WebGL-контекст потерян (см. tasks/map-webgl-context-loss.md, GlitchTip
         FRONTEND-C/D/E) - без этого рендер-цикл MapLibre продолжал бы падать
         на каждом кадре необработанным исключением. z-40, чтобы перекрыть
         .map-controls (z-30). -->
    <div
      v-if="mapContextLost"
      class="absolute inset-0 z-40 flex flex-col items-center justify-center gap-3 bg-[hsl(var(--background)/0.92)] text-center px-6"
    >
      <p class="text-[13px] font-semibold max-w-xs">{{ t('map.contextLost.message') }}</p>
      <button
        type="button"
        class="h-9 px-4 rounded-full bg-[hsl(var(--primary))] text-white text-[12.5px] font-semibold hover:bg-[hsl(158,38%,30%)] transition-colors"
        @click="recreateMap"
      >
        {{ t('map.contextLost.retry') }}
      </button>
    </div>
    <!-- Обычные кнопки управления видом (линейка/карандаш/дебаг/3D/локация/
         компас) прячутся в режиме редактора маршрутов - у него своя верхняя
         панель (меню "Вид" там же дублирует 3D-переключатель). -->
    <!-- Тулбар карты - components/map/MapToolbar.vue (порядок и группы там). -->
    <MapToolbar
      v-if="!routeEditor.active.value"
      :tool-items="toolMenuItems"
      :tools-active="rulerActive || pointTool.active.value || editMode || debugPanelOpen"
      :rotation-mode="mapRotationMode"
      :rotation-label="rotationButtonLabel"
      :has-location="hasLocation"
      :locating="locating"
      :approximate="locationSource === 'ip'"
      :is3D="is3D"
      @pick-tool="onToolPick"
      @cycle-rotation="cycleRotationMode"
      @locate="handleLocateClick"
      @toggle-3d="toggle3D"
    />
    <!-- Язык на мобильном: боковой панели с переключателем здесь нет.
         Рядом с кнопкой атрибуции (верх справа), мелко - чтобы не перекрыть её. -->
    <!-- Верхний правый ряд на мобильном: тема, атрибуция, язык - одна высота и одно
         скругление (CSS .map-top-controls). Атрибуция - кнопка «i», открывает
         окно с полным текстом; сам компактный блок MapLibre на мобильном
         прячется после первого движения карты (CSS), так что до первого касания
         кредит виден без взаимодействия - см. правила OSMF по атрибуции. -->
    <div class="map-top-controls md:hidden">
      <ThemeToggle class="map-top-controls__btn" />
      <button
        type="button"
        class="map-top-controls__btn"
        :aria-label="t('map.attribution.button')"
        :title="t('map.attribution.button')"
        @click="openAttribution"
      >
        <Info class="w-[18px] h-[18px]" />
      </button>
      <div class="map-top-controls__lang"><LanguageSwitcher open-down /></div>
    </div>
    <!-- В body, а не внутри .map-shell: иначе нижний таб-бар (другой контекст
         наложения, z-50) оказывается поверх окна. -->
    <Teleport to="body">
    <div
      v-if="attributionOpen"
      class="fixed inset-0 z-[300] flex items-center justify-center bg-black/40 p-4"
      @click.self="attributionOpen = false"
    >
      <div class="w-full max-w-sm rounded-xl bg-[hsl(var(--card))] p-4 shadow-xl" role="dialog" aria-modal="true" :aria-label="t('map.attribution.title')">
        <div class="flex items-center justify-between gap-2">
          <h3 class="text-[14px] font-semibold">{{ t('map.attribution.title') }}</h3>
          <button
            type="button"
            class="shrink-0 w-8 h-8 rounded-full flex items-center justify-center hover:bg-[hsl(var(--muted))]"
            :aria-label="t('map.attribution.close')"
            @click="attributionOpen = false"
          >
            <XIcon class="w-4 h-4" />
          </button>
        </div>
        <!-- html - тот же текст, что собирает MapLibre из attribution источников
             нашего стиля (map/basemapLayers.ts), единственный источник правды. -->
        <div class="mt-2 space-y-1 text-[13px] leading-snug map-attribution-text">
          <div v-for="(line, i) in attributionLines" :key="i">
            <p v-if="line.label" class="font-semibold">{{ line.label }}</p>
            <p :class="line.label && 'pl-4'" v-html="line.html" />
          </div>
        </div>
        <p class="mt-2 text-[12px] text-[hsl(var(--muted-foreground))]">{{ t('map.attribution.licence') }}</p>
      </div>
    </div>
    </Teleport>
    <p v-if="pointToast.message.value" role="status" class="map-edit-toast">{{ pointToast.message.value }}</p>
    <div v-if="pointTool.point.value && !routeEditor.active.value" class="point-sheet-anchor">
      <MapPointSheet
        :point="pointTool.point.value"
        :marker="pointTool.selectedMarker.value"
        :authed="auth.isAuthenticated"
        :busy="pointTool.busy.value"
        @close="pointTool.close()"
        @login="emit('show-login')"
        @save="pointTool.save($event)"
        @delete="pointTool.removeSelected()"
        @routes-here="pointTool.routesHere()"
        @start-here="pointTool.startHere()"
      />
    </div>
    <component
      :is="PoiOverrideEditor"
      v-if="editorOpen && editorFeature"
      :feature="editorFeature"
      :saving="editorSaving"
      :style="editorPopupStyle"
      class="poi-override-editor-anchor"
      @save="handleEditorSave"
      @revert="handleEditorRevert"
      @cancel="closeEditor"
    />
    <p v-if="editorToastMessage" role="status" class="map-edit-toast">{{ editorToastMessage }}</p>
    <p v-if="routeEditor.notice.value" role="status" class="map-edit-toast">{{ routeEditor.notice.value }}</p>
    <!-- Панель инструментов отладки (при включённом «жучке») — намеренно без vue-i18n. -->
    <div v-if="debugPanelOpen && auth.isAdmin" class="map-debug-tools">
      <button
        type="button"
        class="map-debug-tools__btn"
        :class="{ 'map-debug-tools__btn--on': demCoverageOn }"
        title="Покрытие DEM-тайлами"
        @click="toggleDemCoverage"
      >
        <Grid3x3 class="map-debug-tools__icon" />
      </button>
    </div>
    <!-- Отладочная панель для подбора стилей — намеренно без vue-i18n. -->
    <div v-if="debugPanelOpen && auth.isAdmin" class="map-debug-panel">
      <div class="map-debug-panel__row">
        <span class="map-debug-panel__label">zoom</span>
        <span class="map-debug-panel__value">{{ debugZoom.toFixed(2) }}</span>
      </div>
      <div class="map-debug-panel__row">
        <span class="map-debug-panel__label">center</span>
        <span class="map-debug-panel__value">{{ debugCenter }}</span>
      </div>
      <div v-if="debugFeatures.length === 0" class="map-debug-panel__hint">
        клик по карте покажет атрибуты объекта в этой точке
      </div>
      <div v-for="(f, i) in debugFeatures" :key="i" class="map-debug-panel__feature">
        <div class="map-debug-panel__feature-layer">{{ f.layer }}</div>
        <pre class="map-debug-panel__feature-props">{{ formatDebugProps(f.properties) }}</pre>
      </div>
    </div>

    <!-- Линейка: выноска у кнопки с итогами по всей линии -->
    <div v-if="rulerActive" class="ruler-callout">
      <!-- Предупреждение о неудачной маршрутизации - "выезжает" из-под
           панели вверх (как отдельная карточка позади неё), а не занимает
           место в самой панели, и сама уезжает обратно по таймауту
           (ruler.setRouteError/ROUTE_ERROR_DISPLAY_MS). -->
      <Transition name="ruler-error-card">
        <div v-if="ruler.routeError.value" class="ruler-error-card">
          <TriangleAlert class="ruler-error-card__icon" />
          <span>{{ t(ruler.routeErrorBlocked.value ? 'map.ruler.routeBlocked' : 'map.ruler.routingFallback') }}</span>
        </div>
      </Transition>
      <div class="ruler-callout__head">
        <Ruler class="ruler-callout__icon" />
        <span class="ruler-callout__title">{{ t('map.ruler.title') }}</span>
        <button
          type="button"
          class="ruler-callout__close"
          :aria-label="t('map.ruler.close')"
          @click="ruler.deactivate()"
        >
          <XIcon class="ruler-callout__close-icon" />
        </button>
      </div>
      <p v-if="ruler.vertices.value.length < 2" class="ruler-callout__hint">
        {{ t('map.ruler.hint') }}
      </p>
      <div class="ruler-callout__tools">
        <button
          type="button"
          class="ruler-callout__magnet"
          :class="{ 'ruler-callout__magnet--on': ruler.magnetEnabled.value }"
          :disabled="ruler.routingPending.value"
          :aria-pressed="ruler.magnetEnabled.value"
          :aria-label="t('map.ruler.magnet')"
          :title="t('map.ruler.magnet')"
          @click="ruler.magnetEnabled.value = !ruler.magnetEnabled.value"
        >
          <Loader2 v-if="ruler.routingPending.value" class="ruler-callout__tool-icon locate-btn__icon--spin" />
          <Magnet v-else class="ruler-callout__tool-icon" />
        </button>
        <button
          type="button"
          class="ruler-callout__undo"
          :disabled="ruler.routingPending.value || !ruler.canUndo.value"
          :aria-label="t('map.ruler.undo')"
          :title="t('map.ruler.undo')"
          @click="ruler.undo()"
        >
          <Undo2 class="ruler-callout__tool-icon" />
        </button>
        <button
          type="button"
          class="ruler-callout__undo"
          :disabled="ruler.routingPending.value || !ruler.canRedo.value"
          :aria-label="t('map.ruler.redo')"
          :title="t('map.ruler.redo')"
          @click="ruler.redo()"
        >
          <Redo2 class="ruler-callout__tool-icon" />
        </button>
        <button
          v-if="ruler.vertices.value.length >= 2"
          type="button"
          class="ruler-callout__clear"
          :aria-label="t('map.ruler.clear')"
          :title="t('map.ruler.clear')"
          @click="ruler.clearVertices()"
        >
          <Trash2 class="ruler-callout__tool-icon" />
        </button>
      </div>
      <template v-if="ruler.vertices.value.length >= 2">
        <button
          type="button"
          class="ruler-callout__details-toggle"
          :aria-expanded="rulerDetailsExpanded"
          @click="rulerDetailsExpanded = !rulerDetailsExpanded"
        >
          <span>{{ t('map.ruler.details') }}</span>
          <ChevronDown
            class="ruler-callout__chevron"
            :class="{ 'ruler-callout__chevron--open': rulerDetailsExpanded }"
          />
        </button>
        <div
          class="ruler-callout__details"
          :style="{ gridTemplateRows: rulerDetailsExpanded ? '1fr' : '0fr' }"
        >
          <div class="ruler-callout__details-inner">
            <div class="ruler-callout__row ruler-callout__row--total">
              <span>{{ t('map.ruler.total') }}</span>
              <strong>{{ formatDistance(ruler.totals.value?.distanceM ?? 0) }}</strong>
            </div>
            <div class="ruler-callout__row">
              <span>{{ t('map.ruler.gain') }}</span>
              <strong>{{
                ruler.totals.value?.elevationGainM != null
                  ? `↑ ${Math.round(ruler.totals.value.elevationGainM)} м`
                  : '—'
              }}</strong>
            </div>
            <div class="ruler-callout__row">
              <span>{{ t('map.ruler.loss') }}</span>
              <strong>{{
                ruler.totals.value?.elevationLossM != null
                  ? `↓ ${Math.round(ruler.totals.value.elevationLossM)} м`
                  : '—'
              }}</strong>
            </div>
            <p
              v-if="ruler.totals.value && !ruler.totals.value.hasDemCoverage"
              class="ruler-callout__note"
            >
              {{ t('map.ruler.noElevation') }}
            </p>
            <p
              v-else-if="ruler.totals.value && ruler.totals.value.partialCoverage"
              class="ruler-callout__note"
            >
              {{ t('map.ruler.partialElevation') }}
            </p>
          </div>
        </div>
      </template>
    </div>

    <!-- Линейка: меню по правому клику / долгому тапу -->
    <div
      v-if="ruler.menu.value"
      class="ruler-menu"
      :style="{ left: `${ruler.menu.value.x}px`, top: `${ruler.menu.value.y}px` }"
    >
      <button
        v-if="ruler.menu.value.kind === 'line'"
        type="button"
        class="ruler-menu__item"
        @click="ruler.handleMenuSelect('add-vertex')"
      >
        {{ t('map.ruler.menuAddPoint') }}
      </button>
      <button
        v-if="ruler.menu.value.kind === 'line' && ruler.menu.value.legRouted"
        type="button"
        class="ruler-menu__item"
        @click="ruler.handleMenuSelect('straighten-leg')"
      >
        {{ t('map.ruler.menuStraightenLeg') }}
      </button>
      <button
        v-if="ruler.menu.value.kind === 'line' && !ruler.menu.value.legRouted"
        type="button"
        class="ruler-menu__item"
        @click="ruler.handleMenuSelect('magnetize-leg')"
      >
        {{ t('map.ruler.menuMagnetizeLeg') }}
      </button>
      <button
        v-if="ruler.menu.value.kind === 'vertex'"
        type="button"
        class="ruler-menu__item"
        @click="ruler.handleMenuSelect('delete-vertex')"
      >
        {{ t('map.ruler.menuDeletePoint') }}
      </button>
    </div>

    <template v-if="routeEditor.active.value">
      <!-- Нижняя панель редактора (Закрыть/Сохранить/Импорт/Вид) на десктопе -
           внизу карты. На мобильном та же панель живёт в нижнем баре App.vue
           вместо обычной навигации. Инструменты рисования - справа
           (RouteEditorContextPanel). -->
      <!-- Та же колонка, что у панели статистики (RouteEditorBottomPanel): она
           стоит прямо над этой панелью, как над нижним баром на мобильном. -->
      <div class="hidden md:block pointer-events-none fixed bottom-1.5 right-16 z-[31] w-[560px] max-w-[calc(100vw-176px)]">
        <div class="pointer-events-auto rounded-md bg-sheet border border-border shadow-ink">
          <RouteEditorActionBar
            :editor="routeEditor"
            :is3D="is3D"
            @close="handleEditorClose"
            @save="openSaveDialog(false)"
            @save-as="openSaveDialog(true)"
            @export="handleEditorExport"
            @import="handleEditorImportOpen"
            @toggle-3d="toggle3D"
            @new="handleEditorNew"
          />
        </div>
      </div>
      <RouteEditorAddWaypointDialog
        v-if="routeEditor.pendingWaypoint.value"
        :initial-type="routeEditor.lastWaypointType.value"
        @cancel="routeEditor.cancelPendingWaypoint()"
        @confirm="(...args) => routeEditor.confirmPendingWaypoint(...args)"
        @confirm-marker="onEditorMarkerConfirm"
      />
      <RouteEditorWaypointCard
        v-if="selectedEditorWaypoint"
        :key="selectedEditorWaypoint.clientId"
        :waypoint="selectedEditorWaypoint"
        @update="(patch) => routeEditor.updateWaypoint(selectedEditorWaypoint!.clientId, patch)"
        @delete="routeEditor.deleteWaypoint(selectedEditorWaypoint!.clientId)"
        @close="routeEditor.closeWaypointCard()"
      />
      <RouteEditorContextPanel :editor="routeEditor" :is3D="is3D" @toggle-3d="toggle3D" />
      <RouteEditorOnboarding v-if="showOnboarding" @close="closeOnboarding" />
      <RouteEditorBottomPanel
        v-if="routeEditor.showStatsPanel.value"
        :analytics="routeEditor.analytics.value"
        :name="routeEditor.routeMeta.value.name"
        @update:name="onEditorNameInput"
      />
      <ConfirmDialog
        v-if="pendingDiscard"
        :title="t('map.routeEditor.unsavedTitle')"
        :message="t('map.routeEditor.cancelConfirm')"
        :confirm-label="t('map.routeEditor.discard')"
        :cancel-label="t('map.routeEditor.keepEditing')"
        danger
        @cancel="pendingDiscard = null"
        @confirm="confirmDiscard"
      />
      <RouteImportDialog
        v-if="showImportDialog"
        :initial-tab="importDialogTab"
        @cancel="showImportDialog = false"
        @select-route="handleImportSelectRoute"
        @select-gpx="handleImportSelectGpx"
      />
      <RouteSaveDialog
        v-if="showSaveDialog"
        :is-existing="routeEditor.currentRouteId.value != null && !saveDialogAsNew"
        :saving="routeEditor.saving.value"
        :initial="saveDialogInitial"
        @cancel="showSaveDialog = false"
        @confirm="handleEditorSaveConfirm"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import { startVisiblePoll } from "../composables/useVisiblePoll";
import { createSourceErrorTracker } from "../map/sourceReload";
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useRoute as useAppRoute, useRouter as useAppRouter } from "vue-router";
import { useI18n } from "vue-i18n";
// У maplibre-gl нет default export, поэтому импорт через *.
import * as maplibregl from "maplibre-gl";
import type { VectorTileSource } from "maplibre-gl";
// Воркер тайлов MapLibre, собираемый Vite отдельно - без него базовая
// карта пустая в проде (см. пояснение у setWorkerUrl ниже).
import maplibreWorkerUrl from "maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url";
import { Bug, ChevronDown, Grid3x3, Loader2, Info, Magnet, MapPin, Pencil, PenLine, Redo2, Ruler, Trash2, TriangleAlert, Undo2, X as XIcon } from "lucide-vue-next";
import { useAuthStore } from "../store/auth";
import { useLoginGateStore } from "../store/loginGate";
import { onServerEvent } from "../eventStream";
import api from "../api";
import { ROUTE_ARROW_IMAGE_ID, buildRouteLineLayout, buildRouteLinePaint } from "../utils/routeLineStyleMaplibre";
// Часть разбиения MapView.vue (фаза 1 - чистые данные/функции без Vue-
// реактивности, см. frontend/src/map/*.ts).
import {
  ACCURACY_SOURCE,
  BUILDINGS_ADMIN_SOURCE,
  EMPTY_FEATURES,
  glyphsUrl,
  MAP_DEFAULT_CENTER,
  MAP_MAX_BOUNDS,
  POIS_ADMIN_SOURCE,
  POIS_CLUSTER_LAYER,
  ROUTE_SOURCES,
  vectorTileUrl,
  VECTOR_SOURCES,
} from "../map/constants";
import { buildNameField, buildPassLabelField, buildPeakLabelField } from "../map/labels";
import { addBasemapLayers, PROTECTED_FAR_MAX_ZOOM, PROTECTED_LABEL_SOURCE } from "../map/basemapLayers";
import { protectedAreaLabelPoints } from "../map/protectedAreaLabels";
import { applyMapTheme } from "../map/mapTheme";
import { useTheme } from "../composables/useTheme";
import type { MapCtx } from "../map/mapContext";
import { useMapDebugPanel, safeStringify } from "../composables/useMapDebugPanel";
import { useNodeMarkers } from "../composables/useNodeMarkers";
import { useRouteCursor } from "../composables/useRouteCursor";
import { useRouteDrawing } from "../composables/useRouteDrawing";
import MapToolbar from "./map/MapToolbar.vue";
import { useRouteCoverage } from "../composables/useRouteCoverage";
import { useMapOverlaysStore } from "../store/mapOverlays";
import MapPointSheet from "./MapPointSheet.vue";
import { mapSheetInset } from "../composables/useMapSheetInset";

// Шторка выдвинута выше ~42% окна - тулбар карты (42% высоты) под ней, гасим.
const toolsCovered = computed(() => mapSheetInset.value > window.innerHeight * 0.42);
import { splitAttribution, type AttributionLine } from "../utils/attribution";
import LanguageSwitcher from "./LanguageSwitcher.vue";
import ThemeToggle from "./ThemeToggle.vue";
import { usePointTool } from "../composables/usePointTool";
import { useMyMarkersStore } from "../store/myMarkers";
import { markerFromQuery } from "../utils/markerQuery";
import { useToast } from "../composables/useToast";
import { useMapRotation3D } from "../composables/useMapRotation3D";
import { useGpsTracking } from "../composables/useGpsTracking";
import { useSearchLayers } from "../composables/useSearchLayers";
import { useSearchFocusStore } from "../store/searchFocus";
import { EDIT_POINT_LAYERS, POI_CLUSTER_EXCLUDED_KINDS, usePoiLayers } from "../composables/usePoiLayers";
import { dispatchMapClick } from "../map/mapClick";
import { useMapRuler, formatDistance } from "../composables/useMapRuler";
import { useRouteEditor } from "../composables/useRouteEditor";
import { useRoutingPrefetch } from "../composables/useRoutingPrefetch";
import RouteEditorActionBar, { type ImportTab } from "./RouteEditorActionBar.vue";
import RouteEditorAddWaypointDialog from "./RouteEditorAddWaypointDialog.vue";
import RouteEditorBottomPanel from "./RouteEditorBottomPanel.vue";
import RouteEditorContextPanel from "./RouteEditorContextPanel.vue";
import RouteEditorOnboarding from "./RouteEditorOnboarding.vue";
import { dismissOnboarding, isOnboardingDismissed } from "../utils/routeEditorOnboarding";
import ConfirmDialog from "./ConfirmDialog.vue";
import RouteEditorWaypointCard from "./RouteEditorWaypointCard.vue";
import RouteImportDialog from "./RouteImportDialog.vue";
import RouteSaveDialog from "./RouteSaveDialog.vue";
import { downloadGpx } from "../utils/gpxExport";
import { useMyRoutesStore } from "../store/myRoutes";
import type { RouteMapItem } from "../types/routes";
import type { GpxImportWaypoint } from "../composables/useRouteEditor";

console.info("[MapView] setup start");

// ── Воркер тайлов MapLibre ───────────────────────────────────────────────
// MapLibre ищет maplibre-gl-worker.mjs относительно собственного
// import.meta.url. В деве это работает (файл лежит рядом в node_modules), а
// в собранном билде import.meta.url указывает на /assets/index-<hash>.js, и
// такого соседнего файла там нет. Ломается тихо: запрос воркера попадает в
// SPA-фолбэк и приходит как 200 text/html, воркер падает на первом `<`, а
// MapLibre не выдаёт ни ошибки в консоль, ни события `error` - карта просто
// показывает фон/hillshade/маркеры (это работа главного потока), но ни
// одной линии, заливки или подписи (это всё воркер), и выглядит как будто
// не работает сам тайл-сервер. `?worker&url` заставляет Vite собрать этот
// файл как отдельный чанк и вернуть его URL и в деве, и в билде.
maplibregl.setWorkerUrl(maplibreWorkerUrl);

// `locale` наравне с `t`: подписи базовой карты идут не переводными
// строками, а тегами `name:<lang>` из векторных тайлов OSM - см.
// buildNameField / applyLabelLocale ниже.
const { t, locale } = useI18n();
const auth = useAuthStore();
const loginGate = useLoginGateStore();

// ── Режим редактирования карты («карандаш») ─────────────────────────────
// Тот же гейт, что и у остальных admin-фич - зеркалит backend'ный
// require_map_editor (app/core/deps.py).
const canEditMap = computed(() => auth.isAdmin || auth.roles.includes("map_editor"));
const editMode = ref(false);

const props = defineProps<{
  routesVersion?: number;
  selectedRouteId?: number | null;
  // Дистанция (км) вдоль маршрута, где стоит курсор - двусторонняя синхронизация
  // с графиком высот в RouteDetailsPanel через App.vue (см. utils/routeCursor.ts).
  // null = курсор не выставлен.
  cursorDistanceKm?: number | null;
}>();
const emit = defineEmits<{
  (e: "route-select", routeId: number): void;
  (e: "open-camera", cameraId: number): void;
  (e: "open-node", nodeId: number): void;
  (e: "update:cursorDistanceKm", km: number | null): void;
  // Тап по пустой карте (не по точке, линии маршрута, маркеру/попапу - см.
  // map/mapClick.ts и handleMapClickForCursor) или начало перетаскивания
  // карты: App.vue сворачивает открытую мобильную карточку до заголовка.
  (e: "map-interact"): void;
  // Тап по точке POI вне режима редактирования: id "osm:<id>" / "hiking:<id>".
  (e: "point-tap", id: string): void;
  // «Своя метка» без входа - предлагаем войти.
  (e: "show-login"): void;
  // Нижняя панель точки открыта/закрыта: на мобильном App.vue прячет на это
  // время шторки маршрута/списка, иначе они ложатся друг на друга.
  (e: "point-sheet-change", open: boolean): void;
}>();


const mapEl = ref<HTMLDivElement | null>(null);
// Обычная переменная, не ref: MapLibre Map - большой WebGL-объект, оборачивать
// его в глубокий реактивный прокси Vue не нужно (в шаблоне он не читается).
let map: maplibregl.Map | null = null;
let mapReady = false;
// Реактивный сигнал «карта готова» для вотчеров, которым нужен экземпляр карты.
const mapReadyTick = ref(0);
// GlitchTip FRONTEND-C/D/E (tasks/map-webgl-context-loss.md): "Could not
// compile fragment shader: null" - тот самый буквальный "null" в логе шейдера
// это подпись именно потери WebGL-контекста (GPU-процесс упал/переиспользован
// браузером), не ошибки в самом шейдере. Без обработки webglcontextlost
// рендер-цикл MapLibre продолжает пытаться рисовать и падает на каждом кадре
// необработанным исключением - карта просто зависает без единой подсказки.
const mapContextLost = ref(false);
const refreshIntervalMs = 30000;
let stopNodesPoll: (() => void) | null = null;
let initTimeout: number | null = null;
let resizeObserver: ResizeObserver | null = null;

// ── Общий контекст composable'ов (фаза 2 разбиения) ─────────────────────
// getMap/isMapReady читают `map`/`mapReady` заново при каждом вызове (см.
// пояснение в map/mapContext.ts) - на момент создания ctx ниже tryInitMap ещё
// не отработал. ctx.pushDebug временно no-op и заменяется настоящей функцией
// сразу после useMapDebugPanel(ctx) - остальные composable'ы читают его как
// `ctx.pushDebug(...)`, а не через захваченную на старте константу, поэтому
// порядок безопасен независимо от того, что вызвано раньше.
const ctx: MapCtx = {
  getMap: () => map,
  isMapReady: () => mapReady,
  t,
  locale,
  editMode,
  canEditMap,
  pushDebug: () => {},
  isRulerActive: () => false,
};

const {
  debugPanelOpen,
  debugZoom,
  debugCenter,
  debugFeatures,
  pushDebug,
  updateDebugZoom,
  handleDebugClick,
  formatDebugProps,
} = useMapDebugPanel(ctx);
ctx.pushDebug = pushDebug;

const {
  nodeMarkers,
  fetchNodes,
  applyNodeZoomVisibility,
  maybeCenterInitially,
  markMapInteraction,
} = useNodeMarkers(ctx, {
  hasSelectedRouteId: () => typeof props.selectedRouteId === "number",
  windUnit: () => t("units.ms"),
});

// ── Курсор маршрута / отрисовка маршрута ─────────────────────────────────
// Взаимная зависимость (курсор читает точки/км маршрута, отрисовка маршрута
// вызывает syncCursorFromProp/removeCursorMarker курсора) разрешена через
// геттеры + отложенный доступ к routeDrawing по замыканию: useRouteCursor
// создаётся первым, но его геттеры вызываются не сейчас, а из будущих
// обработчиков событий - к тому моменту routeDrawingRef уже присвоен.
let routeDrawingRef: ReturnType<typeof useRouteDrawing> | null = null;
// «Действие на карте» (свернуть мобильную карточку) - не при линейке/редакторе
// маршрута: там тапы и перетаскивания - работа инструмента. ruler/routeEditor
// объявлены ниже - читаются только из рантайм-обработчиков.
const emitMapInteract = () => {
  if (ruler.active.value || routeEditor.active.value) return;
  emit("map-interact");
};

const routeCursor = useRouteCursor(ctx, {
  getRoutePoints: () => routeDrawingRef?.getCurrentRoutePointsLLE() ?? [],
  getCursorCumulativeKm: () => routeDrawingRef?.getCursorCumulativeKm() ?? [],
  getRouteProfilePoints: () => routeDrawingRef?.getCurrentRouteProfilePoints() ?? [],
  emitCursorUpdate: (km) => emit("update:cursorDistanceKm", km),
  emitMapTap: () => emitMapInteract(),
});
const { removeCursorMarker, syncCursorFromProp, handleMapClickForCursor } = routeCursor;

const routeDrawing = useRouteDrawing(ctx, {
  getCursorDistanceKmProp: () => props.cursorDistanceKm,
  syncCursorFromProp,
  removeCursorMarker,
});
routeDrawingRef = routeDrawing;
const {
  clearCurrentRoute,
  redrawCurrentRouteLine,
  drawRoute,
  loadSelectedRoute,
  applyRouteZoomVisibility,
  loadRouteStyleConfig,
} = routeDrawing;

// ── Поворот карты / 3D + GPS ─────────────────────────────────────────────
// Тот же взаимный приём, что и у курсора/отрисовки маршрута выше: rotation
// создаётся первым и получает колбэки на ещё не существующий gps через
// forward-ref замыкание (gpsRef); GPS получает mapRotationMode уже готовым
// Ref-параметром, поскольку читает его, а не вызывает функцию.
let gpsRef: ReturnType<typeof useGpsTracking> | null = null;

const rotation = useMapRotation3D(ctx, {
  attachHeadingListener: () => gpsRef?.attachHeadingListener(),
  getLastHeadingDeg: () => gpsRef?.getLastHeadingDeg() ?? null,
  setUserHeading: (heading) => gpsRef?.setUserHeading(heading),
});
const { mapRotationMode, is3D, rotationButtonLabel, cycleRotationMode, toggle3D } = rotation;

const searchFocus = useSearchFocusStore();
const gps = useGpsTracking(ctx, {
  mapRotationMode,
  maybeCenterInitially,
  markProgrammaticRotate: rotation.markProgrammaticRotate,
  onGpsPosition: (lat, lng) => searchFocus.setUserPosition({ lat, lon: lng }),
});
gpsRef = gps;
const { locating, hasLocation, locationSource, handleLocateClick } = gps;

// Результат поиска (пин/линия) и публикация центра карты для поиска.
useSearchLayers(ctx);

// ── POI-слои (пины/кластеры/чипы + редактирование карты) ─────────────────
// MERGED-composable - см. подробное объяснение цикла вызовов в
// usePoiLayers.ts. getLabelLang - отложенный доступ к labelLang() ниже (тот
// же принцип, что и у остальных forward-ref/геттеров в этом файле: сам вызов
// происходит не сейчас, а из будущих обработчиков 'idle', когда labelLang
// уже объявлен).
const poiLayers = usePoiLayers(ctx, {
  getLabelLang: () => labelLang(),
  onPointTap: (id) => emit("point-tap", id),
});

// ── Тематические слои меню «Слои»: нацпарки и покрытие троп маршрутами ──
const overlays = useMapOverlaysStore();
const routeCoverage = useRouteCoverage(ctx, {
  enabled: () => overlays.routeCoverage,
  onSelect: (routeId) => emit("route-select", routeId),
  // Под стеком выбранного маршрута: выбранный всегда рисуется поверх покрытия.
  beforeLayerId: ROUTE_SOURCES.glow,
});
watch(() => overlays.routeCoverage, () => routeCoverage.refresh());
// Границы охраняемых территорий (basemapLayers.ts): далеко заливка + граница +
// подпись, близко только красная граница; скрыты по умолчанию.
// Тема карты следует теме приложения (map/mapTheme.ts).
const { isDark } = useTheme();
watch(isDark, (dark) => {
  const m = ctx.getMap();
  if (m) applyMapTheme(m, dark);
});
const PROTECTED_AREA_LAYERS = ["boundaries-fill", "boundaries-line", "boundaries-line-near", "boundaries-label"];
const applyProtectedAreas = () => {
  const m = ctx.getMap();
  for (const id of PROTECTED_AREA_LAYERS) {
    if (m?.getLayer(id)) m.setLayoutProperty(id, "visibility", overlays.protectedAreas ? "visible" : "none");
  }
};
watch(() => overlays.protectedAreas, () => {
  applyProtectedAreas();
  refreshProtectedAreaLabels();
});
// Одна подпись на парк: куски парка из тайлов склеиваем по имени и ставим точку
// в центр той части парка, что сейчас на экране (map/protectedAreaLabels.ts).
let protectedLabelsPayload = "";
const refreshProtectedAreaLabels = () => {
  const m = ctx.getMap();
  const src = m?.getSource(PROTECTED_LABEL_SOURCE) as maplibregl.GeoJSONSource | undefined;
  if (!m || !src) return;
  let data: unknown = { type: "FeatureCollection", features: [] };
  if (overlays.protectedAreas && m.getZoom() < PROTECTED_FAR_MAX_ZOOM) {
    const features = m.querySourceFeatures("boundaries", { sourceLayer: "boundaries", filter: ["==", ["get", "kind"], "protected_area"] });
    const b = m.getBounds();
    data = protectedAreaLabelPoints(features as never, [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()]);
  }
  // idle приходит часто - не пересобираем источник, если точки те же.
  const payload = JSON.stringify(data);
  if (payload === protectedLabelsPayload) return;
  protectedLabelsPayload = payload;
  // Чистый JSON: свойства из querySourceFeatures не всегда сериализуются воркером как есть.
  src.setData(JSON.parse(payload));
};
const {
  PoiOverrideEditor,
  editorOpen,
  editorSaving,
  editorFeature,
  editorScreen,
  editorToastMessage,
  editorPopupStyle,
  closeEditor,
  handleEditorEscape,
  updateEditorScreenPos,
  handlePoiEditClick,
  handleEditorSave,
  handleEditorRevert,
  toggleEditMode,
  setClusterCursorPointer,
  clearClusterCursorPointer,
  handlePoiHover,
  handleClusterHover,
  hidePoiHoverTooltip,
  refreshPoiPinMarkers,
  refreshPoiClusterSource,
} = poiLayers;

// ── Линейка ─────────────────────────────────────────────────────────────
const ruler = useMapRuler(ctx);
const rulerActive = ruler.active;
// Общий гейт для usePoiLayers.ts/mapUiHelpers.ts (клики/попапы POI не должны
// мешать рисованию) - namе "isRulerActive" исторический, но семантика уже
// "активен ЛЮБОЙ инструмент рисования по клику на карте"; routeEditor
// объявляется чуть ниже в этом же setup, forward-reference в замыкании
// безопасен - вызывается только во время рантайм-кликов, не при определении.
ctx.isRulerActive = () => ruler.active.value || routeEditor.active.value || pointTool.active.value;
// Блок с итогами (длина/набор/потеря) сворачиваемый, развёрнут по умолчанию -
// см. .ruler-callout__details в styles.css.
const rulerDetailsExpanded = ref(true);

// ── Редактор маршрутов ────────────────────────────────────────────────
// Отдельный composable, не завязанный на useMapRuler.ts (см. его шапку) -
// но делит с линейкой одну карту, поэтому оба инструмента взаимно
// исключающие: включение одного выключает другой (ниже, в обработчиках
// кнопок), иначе оба слушателя map "click" сработали бы на одном клике.
const routeEditor = useRouteEditor(ctx);
// Тайлы графа вокруг центра карты - заранее, пока активны редактор или линейка.
useRoutingPrefetch(ctx, computed(() => routeEditor.active.value || ruler.active.value));

// ── Инструмент «Метка» ──────────────────────────────────────────────────
// Взаимно исключающий с линейкой и редактором (один слушатель map "click"
// на жест) - см. onPointToolToggleClick/onRulerToggleClick ниже.
const attributionOpen = ref(false);
const attributionLines = ref<AttributionLine[]>([]);
const openAttribution = () => {
  attributionLines.value = splitAttribution(
    mapEl.value?.querySelector(".maplibregl-ctrl-attrib-inner")?.innerHTML ??
      '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
  );
  attributionOpen.value = true;
};
const pointToast = useToast();
const pointTool = usePointTool(ctx, {
  onError: (message) => pointToast.show(message),
  openEditorAt: (lngLat, start) => {
    pointTool.deactivate();
    if (ruler.active.value) ruler.deactivate();
    if (!routeEditor.active.value) routeEditor.activate();
    const m = ctx.getMap();
    m?.easeTo({ center: lngLat, zoom: Math.max(m.getZoom(), 14) });
    if (start && !routeEditor.startAt(lngLat)) pointToast.show(t("map.pointTool.draftExists"));
  },
});
watch(
  () => pointTool.point.value != null && !routeEditor.active.value,
  (open) => emit("point-sheet-change", open)
);
const appRoute = useAppRoute();
const appRouter = useAppRouter();

// ?edit=<id>: «Править в редакторе» из «Я › Мои маршруты» - открываем редактор
// с этим маршрутом (чужой/без входа - не открываем); параметр убираем.
watch(
  () => [appRoute.query.edit, mapReadyTick.value, auth.isAuthenticated] as const,
  async ([value]) => {
    if (value == null || !ctx.getMap()) return;
    const id = Number(Array.isArray(value) ? value[0] : value);
    // Сначала убираем параметр и ДОЖИДАЕМСЯ замены: иначе запись истории
    // редактора (#route-editor) копирует query с edit, параметр возвращается и
    // редактор открывается заново (перезагружая маршрут) после закрытия.
    const { edit: _drop, ...rest } = appRoute.query;
    await appRouter.replace({ path: appRoute.path, query: rest, hash: appRoute.hash });
    if (!Number.isFinite(id)) return;
    loginGate.requireLogin(auth.isAuthenticated, () => {
      requestDiscardThen(async () => {
        if (ruler.active.value) ruler.deactivate();
        pointTool.deactivate();
        if (!routeEditor.active.value) routeEditor.activate();
        await routeEditor.loadRoute(id);
      });
    });
  }
);

// ?marker=<id>: переход из «Я › Мои метки» - центрируем карту на метке и
// открываем её панель; параметр убираем, чтобы возврат на вкладку не
// повторял переход.
const myMarkersStore = useMyMarkersStore();

// «Метка» в диалоге добавления точки редактора - личная заметка (Мои метки),
// не точка маршрута: сохраняем её на бэкенде и закрываем диалог, маршрут не меняется.
const onEditorMarkerConfirm = async (name: string) => {
  const pending = routeEditor.pendingWaypoint.value;
  if (!pending) return;
  const saved = await myMarkersStore.create({ name, kind: "custom", lat: pending.latitude, lng: pending.longitude });
  if (saved) routeEditor.cancelPendingWaypoint();
};
watch(
  () => [appRoute.query.marker, myMarkersStore.items, mapReadyTick.value] as const,
  ([value]) => {
    if (value == null) return;
    const marker = markerFromQuery(value, myMarkersStore.items);
    const m = ctx.getMap();
    if (!marker || !m) return;
    m.easeTo({ center: [marker.lng, marker.lat], zoom: Math.max(m.getZoom(), 15) });
    pointTool.selectMarker(marker);
    const { marker: _drop, ...rest } = appRoute.query;
    void appRouter.replace({ path: appRoute.path, query: rest, hash: appRoute.hash });
  },
  { deep: true }
);

// Подтверждение потери несохранённых изменений - модал (ConfirmDialog), а не
// window.confirm. pendingDiscard - действие, которое выполнится после
// "Выйти без сохранения"; пока оно не null - модал открыт.
const pendingDiscard = ref<(() => void) | null>(null);
const requestDiscardThen = (action: () => void) => {
  if (!routeEditor.active.value || !routeEditor.dirty.value) {
    action();
    return;
  }
  pendingDiscard.value = action;
};
const confirmDiscard = () => {
  const action = pendingDiscard.value;
  pendingDiscard.value = null;
  action?.();
};

// Кнопка "Назад" браузера. Режим редактора занимает собственную запись в
// истории (#route-editor): "Назад" закрывает редактор, а не уходит со
// страницы, и при несохранённых изменениях сперва спрашивает. Ушедшая на
// запись навигация (в т.ч. тап по другой вкладке) тоже проходит через guard.
// Онбординг при первом открытии редактора (пока не отмечено "больше не
// показывать").
const showOnboarding = ref(false);
const closeOnboarding = (dontShowAgain: boolean) => {
  showOnboarding.value = false;
  if (dontShowAgain) dismissOnboarding();
};
watch(
  () => routeEditor.active.value,
  (on) => {
    if (on && !isOnboardingDismissed()) showOnboarding.value = true;
    if (!on) showOnboarding.value = false;
  }
);

const EDITOR_HASH = "#route-editor";
let editorEntryPushed = false;
let allowEditorLeave = false;
const removeEditorGuards: Array<() => void> = [];
watch(
  () => routeEditor.active.value,
  (on) => {
    if (on) {
      if (appRoute.hash !== EDITOR_HASH) {
        editorEntryPushed = true;
        void appRouter.push({ path: appRoute.path, query: appRoute.query, hash: EDITOR_HASH });
      }
    } else if (editorEntryPushed) {
      editorEntryPushed = false;
      if (appRoute.hash === EDITOR_HASH) {
        allowEditorLeave = true;
        appRouter.back();
      }
    }
  }
);
removeEditorGuards.push(
  appRouter.beforeEach((to, from) => {
    if (allowEditorLeave || !routeEditor.active.value) return true;
    if (from.hash !== EDITOR_HASH || to.hash === EDITOR_HASH) return true;
    if (!routeEditor.dirty.value) {
      // Редактор чистый - закрываем, навигация проходит как обычно.
      editorEntryPushed = false;
      routeEditor.deactivate();
      return true;
    }
    pendingDiscard.value = () => {
      const previous = (window.history.state as { back?: string | null } | null)?.back;
      if (to.fullPath === previous) {
        // Это и была "Назад": деактивация вернёт историю на запись назад.
        routeEditor.deactivate();
      } else {
        editorEntryPushed = false;
        routeEditor.deactivate();
        void appRouter.push(to.fullPath);
      }
    };
    return false;
  }),
  appRouter.afterEach(() => {
    allowEditorLeave = false;
  })
);
// Закрытие вкладки/перезагрузка с несохранёнными изменениями - только
// нативный запрос браузера (модал там невозможен).
const onBeforeUnload = (e: BeforeUnloadEvent) => {
  if (routeEditor.active.value && routeEditor.dirty.value) {
    e.preventDefault();
    e.returnValue = "";
  }
};
window.addEventListener("beforeunload", onBeforeUnload);
onBeforeUnmount(() => {
  window.removeEventListener("beforeunload", onBeforeUnload);
  removeEditorGuards.forEach((remove) => remove());
});

const onRulerToggleClick = () => {
  if (!ruler.active.value && routeEditor.active.value) routeEditor.deactivate();
  if (!ruler.active.value) pointTool.deactivate();
  ruler.toggle();
};
const onPointToolToggleClick = () => {
  if (!pointTool.active.value && ruler.active.value) ruler.deactivate();
  pointTool.toggle();
};

// Пункты меню «Инструменты»; редактор - только для вошедших (гость по клику
// попадает на окно входа, пометки в самом пункте нет).
const toolMenuItems = computed(() => [
  { key: "ruler", label: t("map.ruler.button"), icon: Ruler, active: rulerActive.value },
  { key: "point", label: t("map.pointTool.button"), icon: MapPin, active: pointTool.active.value },
  {
    key: "editor",
    label: t("map.routeEditor.button"),
    icon: PenLine,
    active: routeEditor.active.value,
  },
  // Ролевые инструменты - пунктами того же меню, не отдельными кнопками.
  { key: "mapEdit", label: t("map.editMode"), icon: Pencil, active: editMode.value, hidden: !canEditMap.value },
  { key: "debug", label: t("map.debugPanel"), icon: Bug, active: debugPanelOpen.value, hidden: !auth.isAdmin },
]);
const onToolPick = (key: string) => {
  if (key === "mapEdit") toggleEditMode();
  else if (key === "debug") debugPanelOpen.value = !debugPanelOpen.value;
  else if (key === "ruler") onRulerToggleClick();
  else if (key === "point") onPointToolToggleClick();
  else if (key === "editor") loginGate.requireLogin(auth.isAuthenticated, onRouteEditorToggleClick);
};
const onRouteEditorToggleClick = () => {
  if (routeEditor.active.value) {
    requestDiscardThen(() => routeEditor.deactivate());
    return;
  }
  if (ruler.active.value) ruler.deactivate();
  pointTool.deactivate();
  routeEditor.activate();
};

const myRoutesStore = useMyRoutesStore();
// Название прямо в нижней панели редактора (диалог сохранения подхватывает
// его же из routeMeta).
const onEditorNameInput = (value: string) => {
  routeEditor.routeMeta.value.name = value;
  routeEditor.dirty.value = true;
};
const showImportDialog = ref(false);
const showSaveDialog = ref(false);
const saveDialogAsNew = ref(false);

const selectedEditorWaypoint = computed(() =>
  routeEditor.waypoints.value.find((w) => w.clientId === routeEditor.selectedWaypointId.value) || null
);

const handleEditorNew = () => requestDiscardThen(() => routeEditor.newRoute());
const importDialogTab = ref<ImportTab>("mine");
const handleEditorImportOpen = (tab: ImportTab = "mine") => {
  importDialogTab.value = tab;
  requestDiscardThen(() => {
    showImportDialog.value = true;
  });
};
const handleImportSelectRoute = async (route: RouteMapItem, asNewCopy: boolean) => {
  showImportDialog.value = false;
  await routeEditor.loadRoute(route.id, { asNewCopy });
};
const handleImportSelectGpx = (name: string, points: [number, number][], waypoints: GpxImportWaypoint[]) => {
  showImportDialog.value = false;
  routeEditor.loadFromGpxImport(name, points, waypoints);
};
const openSaveDialog = (asNew: boolean) => {
  saveDialogAsNew.value = asNew;
  showSaveDialog.value = true;
};
const saveDialogInitial = computed(() => {
  const meta = routeEditor.routeMeta.value;
  if (saveDialogAsNew.value && routeEditor.currentRouteId.value != null) {
    return { name: `${meta.name} (копия)`.trim(), description: meta.description, tags: meta.tags, is_public: false };
  }
  return meta;
});
const handleEditorSaveConfirm = async (meta: { name: string; description: string | null; tags: string[]; is_public: boolean }) => {
  const result = await routeEditor.save(meta, { asNew: saveDialogAsNew.value });
  if (result) {
    showSaveDialog.value = false;
    myRoutesStore.invalidate();
  }
};
const handleEditorExport = () => {
  const points = routeEditor.exportCoordinates();
  if (points.length < 2) return;
  downloadGpx(
    routeEditor.routeMeta.value.name || t("map.routeEditor.newRoute"),
    points,
    routeEditor.waypoints.value.map((w) => ({
      name: w.name,
      description: w.description,
      poi_type: w.poi_type,
      latitude: w.latitude,
      longitude: w.longitude,
    }))
  );
};
const handleEditorClose = () => requestDiscardThen(() => routeEditor.deactivate());

const handleRulerKeydown = (e: KeyboardEvent) => {
  if (!ruler.active.value) return;
  if (e.key === "Escape") {
    ruler.deactivate();
    return;
  }
  // Ctrl+Z (Cmd+Z на Mac) - отменить, Ctrl+Shift+Z / Ctrl+Y - повторить,
  // Backspace - удалить последнюю точку. Не перехватываем, если фокус в
  // поле ввода где-то ещё на странице.
  const target = e.target as HTMLElement | null;
  if (target?.tagName === "INPUT" || target?.tagName === "TEXTAREA" || target?.isContentEditable) return;
  const mod = e.ctrlKey || e.metaKey;
  const key = e.key.toLowerCase();
  if (mod && key === "z" && e.shiftKey) {
    e.preventDefault();
    ruler.redo();
    return;
  }
  if (mod && key === "z") {
    e.preventDefault();
    ruler.undo();
    return;
  }
  if (mod && key === "y") {
    e.preventDefault();
    ruler.redo();
    return;
  }
  if (e.key === "Backspace") {
    e.preventDefault();
    ruler.deleteLastVertex();
  }
};

// ── Отладка: оверлей покрытия DEM-тайлами ───────────────────────────────
const DEM_COVERAGE_SOURCE = "dem-coverage";
const demCoverageOn = ref(false);
let demCoverageLoaded = false;

const ensureDemCoverageLayers = () => {
  if (!map || demCoverageLoaded) return;
  map.addSource(DEM_COVERAGE_SOURCE, { type: "geojson", data: EMPTY_FEATURES });
  map.addLayer({
    id: "dem-coverage-fill",
    type: "fill",
    source: DEM_COVERAGE_SOURCE,
    layout: { visibility: "none" },
    paint: { "fill-color": "#1f7a5c", "fill-opacity": 0.12 },
  });
  map.addLayer({
    id: "dem-coverage-line",
    type: "line",
    source: DEM_COVERAGE_SOURCE,
    layout: { visibility: "none" },
    paint: { "line-color": "#1f7a5c", "line-width": 1.5, "line-dasharray": [3, 2] },
  });
  map.addLayer({
    id: "dem-coverage-label",
    type: "symbol",
    source: DEM_COVERAGE_SOURCE,
    layout: {
      visibility: "none",
      "symbol-placement": "point",
      "text-field": ["get", "tile_name"],
      "text-font": ["Noto Sans Regular"],
      "text-size": 11,
    },
    paint: { "text-color": "#0f3d2c", "text-halo-color": "#ffffff", "text-halo-width": 1.6 },
  });
  demCoverageLoaded = true;
};

const setDemCoverageVisible = (visible: boolean) => {
  if (!map) return;
  const v = visible ? "visible" : "none";
  for (const id of ["dem-coverage-fill", "dem-coverage-line", "dem-coverage-label"]) {
    if (map.getLayer(id)) map.setLayoutProperty(id, "visibility", v);
  }
};

const toggleDemCoverage = async () => {
  demCoverageOn.value = !demCoverageOn.value;
  if (!map) return;
  ensureDemCoverageLayers();
  if (!demCoverageOn.value) {
    setDemCoverageVisible(false);
    return;
  }
  try {
    const { data } = await api.get<{
      tiles: { tile_name: string; geometry: GeoJSON.Polygon; area_km2: number }[];
    }>("/terrain/coverage");
    const features = data.tiles.map((t) => ({
      type: "Feature" as const,
      properties: { tile_name: t.tile_name, area_km2: t.area_km2 },
      geometry: t.geometry,
    }));
    (map.getSource(DEM_COVERAGE_SOURCE) as maplibregl.GeoJSONSource | undefined)?.setData({
      type: "FeatureCollection",
      features,
    });
    setDemCoverageVisible(true);
  } catch {
    demCoverageOn.value = false;
  }
};

let fetchTimeout: number | null = null;

// navigator.clipboard требует secure context (https или http://localhost) -
// приложение доступно и по обычному http в локальной сети (см. api.ts), где
// его просто нет. document.execCommand устарел, но остаётся рабочим
// фолбэком для этого случая.
const copyTextToClipboard = async (text: string): Promise<boolean> => {
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // переходим к устаревшему фолбэку ниже
    }
  }
  try {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
};


const scheduleFetch = (delay = 0) => {
  if (fetchTimeout) {
    clearTimeout(fetchTimeout);
  }
  fetchTimeout = window.setTimeout(() => {
    fetchNodes();
  }, delay);
};

// iOS Safari (и особенно standalone/home-screen PWA) приостанавливает
// запросы тайлов в полёте, когда приложение уходит в фон - пользователи iOS
// видят это как тайлы, которые «иногда просто не загружаются».
//
// MapLibre не переповторяет ошибочный тайл сам, поэтому дыра остаётся, пока
// её не форсировать. setTiles - единственный доступный рычаг (тайл - это
// состояние GL/воркера, не DOM-узел): он идёт через setSourceProperty ->
// load(true), сбрасывая кэш тайлов источника и перезапрашивая - тот же URL
// достаточен. Перезапрашиваем только источники, у которых был сбой тайла:
// pois/buildings отдаются с `max-age=60`, поэтому массовый перезапрос при каждом
// возврате во вкладку превращался в шторм запросов (десятки тысяч в сутки).
const sourceErrors = createSourceErrorTracker();
const handleAppForeground = () => {
  if (document.visibilityState !== "visible") return;
  for (const id of sourceErrors.drain()) {
    if (!(VECTOR_SOURCES as readonly string[]).includes(id)) continue;
    const source = map?.getSource(id) as VectorTileSource | undefined;
    source?.setTiles([vectorTileUrl(id)]);
  }
};

const scheduleInit = (delay = 100) => {
  if (initTimeout) {
    clearTimeout(initTimeout);
  }
  initTimeout = window.setTimeout(() => {
    tryInitMap();
  }, delay);
};

const resolveContainer = (): HTMLDivElement | null => {
  if (mapEl.value) return mapEl.value;
  const fallback = document.querySelector<HTMLDivElement>("[data-map-root]");
  return fallback ?? null;
};

// ── Локализованные подписи ────────────────────────────────────────────────
// У каждого именованного объекта в тайлах до пяти имён: обычный OSM `name`
// (как ввёл локальный картограф - в этом регионе обычно казахский, иногда
// русский или киргизский) плюс `name_ru`/`name_en`/`name_kk`/`name_ky` из
// тегов OSM `name:<lang>`. Какое из них ПОКАЗЫВАТЬ, решается здесь, на
// клиенте - тайл кэшируется и общий для всех зрителей независимо от их языка
// интерфейса (см. Alembic 20260825_05, почему сервер это не резолвит).
//
// Языки, реально присутствующие в тайлах. У `ky` нет UI-локали в приложении,
// но колонка есть (Киргизия - половина импорта), так что будущая киргизская
// локаль заработает без переимпорта. Всё вне этого списка откатывается на
// русский, а не запрашивает у тайла заведомо несуществующий `name_xx`.
const MAP_LABEL_LANGS = ["ru", "en", "kk", "ky"] as const;
const MAP_LABEL_FALLBACK_LANG = "ru";

const labelLang = () => {
  const current = String(locale.value || "").toLowerCase().split("-")[0];
  return (MAP_LABEL_LANGS as readonly string[]).includes(current)
    ? current
    : MAP_LABEL_FALLBACK_LANG;
};

// JS-двойник цепочки coalesce из buildNameField (frontend/src/map/labels.ts)
// для обработчиков клика, читающих результат
// queryRenderedFeatures/querySourceFeatures напрямую, минуя style-выражение -
// тот же приоритет языка, просто вычисляется один раз над реальными
// значениями свойств.
const pickFeatureName = (props: Record<string, unknown>, lang: string): string => {
  const localized = lang === "en" ? undefined : props[`name_${lang}`];
  return String(localized || props.name_en || props.name || "").trim();
};

// Каждый слой, чей текст следует языку интерфейса, и как его пересобрать.
// И addBasemapLayers (первичное создание), и applyLabelLocale (живое
// переключение) читают одну и ту же карту - новый слой подписей нельзя
// добавить в одном месте и забыть про другое. buildPeakLabelField/
// buildPassLabelField (frontend/src/map/labels.ts) замыкаются на `t` -
// оборачиваем их здесь, чтобы карта осталась `(lang: string) => any`.
const LOCALIZED_LABEL_FIELDS: Record<string, (lang: string) => any> = {
  "pois-label": buildNameField,
  "pois-peak": (lang) => buildPeakLabelField(t, lang),
  "pois-pass": (lang) => buildPassLabelField(t, lang),
  "buildings-label": buildNameField,
  "places-label": buildNameField,
  "boundaries-label": buildNameField,
};

// Заново применяет text-field каждого слоя подписей для текущей локали.
// Дёшево и синхронно: setLayoutProperty на symbol-слое пересчитывает
// расстановку по уже загруженным тайлам, смена языка не требует ни одного
// нового запроса тайлов.
const applyLabelLocale = () => {
  if (!map || !mapReady) return;
  const lang = labelLang();
  for (const [layerId, build] of Object.entries(LOCALIZED_LABEL_FIELDS)) {
    if (!map.getLayer(layerId)) continue;
    map.setLayoutProperty(layerId, "text-field", build(lang));
  }
};

// Всё, что раньше выполнялось синхронно сразу после L.map(), здесь должно
// ждать 'load': addSource/addLayer до готовности стиля бросают исключение.
const setupMapLayers = (el: HTMLDivElement) => {
  if (!map) return;
  // MapLibre меряет размер контейнера в момент new maplibregl.Map(...), а
  // resizeObserver (ниже) начинает следить только с этой точки - если на
  // конструкции канвас ещё не устоялся в финальный layout (медленная
  // загрузка/шрифты/сторонние скрипты типа Plausible/Sentry сдвигают
  // раскладку уже ПОСЛЕ await nextTick()), последующих изменений размера
  // может больше не случиться, и карта навсегда остаётся с неверным
  // (обычно 0x0 на момент замера) канвасом - белый/пустой фон без тайлов,
  // видимый только через VPN/relay (test.ecoway.kz), где загрузка страницы
  // ощутимо медленнее прямого локального доступа. Один принудительный
  // resize() здесь, на 'load' (после которого раскладка уже точно устоялась),
  // не полагается на то, поймал ли ResizeObserver реальное изменение.
  map.resize();
  addBasemapLayers(map, { t, lang: labelLang(), poiClusterExcludedKinds: POI_CLUSTER_EXCLUDED_KINDS });
  applyMapTheme(map, isDark.value);

  map.addSource(ACCURACY_SOURCE, { type: "geojson", data: EMPTY_FEATURES });
  map.addLayer({
    id: ACCURACY_SOURCE,
    type: "fill",
    source: ACCURACY_SOURCE,
    // #1f7a5c ~= hsl(158 38% 36%), --primary дизайн-системы. Литерал нужен,
    // так как paint-свойства не читают CSS-переменные.
    paint: { "fill-color": "#1f7a5c", "fill-opacity": 0.12 },
  });

  // Стек снизу вверх: glow под casing под основной линией - естественный
  // порядок для z-упорядоченного списка слоёв MapLibre.
  //
  // Весь стек маршрута вставляется ПЕРЕД trails-line (beforeId), а не поверх
  // всей карты: пунктир тропы должен перекрывать линию/градиент маршрута, а
  // белая заливка дороги (roads-line, ниже trails-line) - нет. POI и подписи
  // остаются выше маршрута, что и нужно.
  const paint = buildRouteLinePaint(routeDrawing.getRouteStyleConfig());
  const layout = buildRouteLineLayout(routeDrawing.getRouteStyleConfig());
  for (const [key, layerPaint] of [
    [ROUTE_SOURCES.glow, paint.glow],
    [ROUTE_SOURCES.casing, paint.casing],
    [ROUTE_SOURCES.main, paint.main],
  ] as const) {
    map.addSource(key, { type: "geojson", data: EMPTY_FEATURES });
    map.addLayer(
      {
        id: key,
        type: "line",
        source: key,
        layout: layout as any,
        paint: layerPaint as any,
      },
      "trails-line",
    );
  }

  map.addSource(ROUTE_SOURCES.arrows, { type: "geojson", data: EMPTY_FEATURES });
  map.addLayer(
    {
      id: ROUTE_SOURCES.arrows,
      type: "symbol",
      source: ROUTE_SOURCES.arrows,
      layout: {
        "icon-image": ROUTE_ARROW_IMAGE_ID,
        // Bearing - направление на местности, стрелки должны поворачиваться
        // вместе с картой, а не оставаться прибитыми к экрану.
        "icon-rotate": ["get", "bearing"],
        "icon-rotation-alignment": "map",
        "icon-allow-overlap": true,
        "icon-ignore-placement": true,
      },
    },
    "trails-line",
  );
  routeDrawing.applyRouteLineStyle();
  routeCoverage.setup(map);
  applyProtectedAreas();
  mapReady = true;
  mapReadyTick.value++;
  // Только для дева, в том же духе, что и __mapViewLogs у pushDebug: иначе
  // bearing/pitch/terrain карты недоступны из консоли или автоматизированного
  // браузера. В продакшен-сборку не попадает.
  if (import.meta.env.DEV || import.meta.env.VITE_APP_ENV === "test") {
    (window as any).__ecowayMap = map;
    (window as any).__ecowayMapState = () => ({
      rotationMode: mapRotationMode.value,
      is3D: is3D.value,
      bearing: map?.getBearing(),
      pitch: map?.getPitch(),
    });
  }

  map.on("zoomend", () => {
    applyNodeZoomVisibility();
    applyRouteZoomVisibility();
  });
  // Порядок разбора клика - map/mapClick.ts (точка важнее линии маршрута).
  map.on("click", (event) =>
    dispatchMapClick(event, {
      rulerActive: () => rulerActive.value,
      // Не ctx.isRulerActive: тот включает и «Метку», а ей шаг 2 нужен.
      routeEditorActive: () => routeEditor.active.value,
      pointTool: pointTool.active.value ? pointTool.onMapClick : null,
      hitsCluster: (e) => !!map?.queryRenderedFeatures(e.point, { layers: [POIS_CLUSTER_LAYER] }).length,
      pointTap: poiLayers.handlePointTap,
      // Покрытие троп: попап «маршруты здесь» - только пока маршрут не выбран,
      // иначе клик по линии двигает курсор выбранного маршрута.
      routeCoverage: (e) => typeof props.selectedRouteId !== "number" && routeCoverage.handleClick(e),
      routeCursor: handleMapClickForCursor,
    })
  );
  // Начало перетаскивания карты - тоже «действие на карте» (свернуть карточку).
  map.on("dragstart", emitMapInteract);
  // 'idle' (тайлы/рендер осели после пана/зума), а не 'moveend' -
  // querySourceFeatures видит только уже загруженные тайлы, запуск против
  // тайлов в полёте просто пропустил бы объекты и всё равно перезапустился
  // бы чуть позже.
  map.on("idle", refreshPoiPinMarkers);
  refreshPoiPinMarkers();
  map.on("idle", refreshPoiClusterSource);
  refreshPoiClusterSource();
  pointTool.resyncAll();
  // Клик по кластеру больше не зумит - курсор остаётся обычным, не pointer.
  // Клик всё ещё не проваливается в handleMapClickForCursor (см. её же
  // проверку по POIS_CLUSTER_LAYER выше) - должен оставаться холостым.
  map.on("mousemove", "pois-icon-unclustered", handlePoiHover);
  // Вершины/перевалы открывают карточку по тапу - курсор pointer, как у иконок
  // (pois-icon-unclustered получает его через EDIT_POINT_LAYERS ниже). В режиме
  // редактирования эти слои скрыты (вместо них -admin).
  for (const layerId of ["pois-peak", "pois-pass"]) {
    map.on("mouseenter", layerId, setClusterCursorPointer);
    map.on("mouseleave", layerId, clearClusterCursorPointer);
  }
  map.on("mouseleave", "pois-icon-unclustered", hidePoiHoverTooltip);
  map.on("mousemove", POIS_CLUSTER_LAYER, handleClusterHover);
  map.on("mouseleave", POIS_CLUSTER_LAYER, hidePoiHoverTooltip);
  // Зум/пан колесом под неподвижным курсором не даёт mousemove - без этого
  // хинт завис бы, указывая в пустоту.
  map.on("movestart", hidePoiHoverTooltip);
  // Если пользователь успел сам подвинуть карту, пока геолокация/список
  // узлов ещё грузятся, maybeCenterInitially (useNodeMarkers.ts) не должен
  // перебивать это прыжком к узлу - см. markMapInteraction.
  map.on("movestart", markMapInteraction);
  // Привязано к слою (см. комментарий EDIT_POINT_LAYERS про структурную
  // гарантию) - каждый обработчик no-op, пока editMode выключен.
  for (const layerId of EDIT_POINT_LAYERS) {
    map.on("click", layerId, handlePoiEditClick);
    map.on("mouseenter", layerId, setClusterCursorPointer);
    map.on("mouseleave", layerId, clearClusterCursorPointer);
  }
  // Держит попап редактора приклеенным к объекту при движении/зуме карты -
  // то же живое отслеживание, что и у настоящего MapLibre Popup бесплатно.
  map.on("move", updateEditorScreenPos);
  map.on("dragstart", closeEditor);
  updateDebugZoom();
  map.on("zoom", updateDebugZoom);
  map.on("move", updateDebugZoom);
  map.on("click", (event) => {
    if (rulerActive.value) return;
    handleDebugClick(event);
  });
  // Держит конус направления указывающим реальный путь, пока карта поворачивается.
  map.on("rotate", gps.refreshUserHeadingVisual);
  map.on("rotatestart", rotation.handleRotateStart);
  map.on("error", (e: any) => {
    sourceErrors.note(e?.sourceId);
    pushDebug("maplibre error", safeStringify(e?.error?.message || e?.error || e));
  });

  scheduleFetch(0);
  stopNodesPoll = startVisiblePoll(() => scheduleFetch(0), refreshIntervalMs);
  if (typeof props.selectedRouteId === "number") {
    loadSelectedRoute(props.selectedRouteId);
  }

  // Сразу запускает маркер/отслеживание «вы здесь» (фолбэк на IP-геолокацию
  // при отказе GPS). Первый фикс также запускает одноразовое центрирование
  // карты (maybeCenterInitially) - маршрут, уже выбранный через URL,
  // приоритетнее и обрабатывается loadSelectedRoute выше.
  gps.startWatchingPosition();

  // Центр карты - точка отсчёта для поиска (search/useSearch.ts).
  const publishCenter = () => {
    const c = ctx.getMap()?.getCenter();
    if (c) searchFocus.setMapCenter({ lat: c.lat, lon: c.lng });
  };
  publishCenter();
  map.on("moveend", publishCenter);
  map.on("moveend", refreshProtectedAreaLabels);
  // idle - когда тайлы границ догрузились (на moveend они ещё могут не прийти).
  map.on("idle", refreshProtectedAreaLabels);

  // Делегированный обработчик клика: содержимое попапа может замениться
  // периодическими обновлениями (setHTML не переигрывает событие open
  // попапа), поэтому привязка прямо к внутренним кнопкам молча теряла бы
  // слушатель. Слушатель на самом контейнере карты переживает любое число
  // замен содержимого.
  el.addEventListener("click", (ev: MouseEvent) => {
    const copyTarget = (ev.target as HTMLElement)?.closest?.("[data-copy-coords]");
    if (copyTarget) {
      const coordsText = copyTarget.getAttribute("data-copy-coords") || "";
      const icon = copyTarget.querySelector(".route-poi-popup__coords-icon");
      copyTextToClipboard(coordsText).then((ok) => {
        if (!ok || !icon) return;
        const original = icon.textContent;
        icon.textContent = "✓";
        copyTarget.classList.add("route-poi-popup__coords--copied");
        setTimeout(() => {
          icon.textContent = original;
          copyTarget.classList.remove("route-poi-popup__coords--copied");
        }, 1200);
      });
      return;
    }
    const target = (ev.target as HTMLElement)?.closest?.("[data-node-live]");
    if (!target) return;
    const cameraId = Number(target.getAttribute("data-camera-id"));
    if (cameraId) {
      emit("open-camera", cameraId);
      return;
    }
    const nodeId = Number(target.getAttribute("data-node-live"));
    if (nodeId) emit("open-node", nodeId);
  });

  resizeObserver = new ResizeObserver(() => {
    // Карта остаётся смонтированной (v-show, не v-if) при переключении
    // вкладок, поэтому её контейнер может уйти в 0x0 и обратно. resize() -
    // эквивалент invalidateSize() из Leaflet.
    map?.resize();
  });
  resizeObserver.observe(el);

  // Масштабная линейка (нижний левый угол) + слои/обработчики инструмента
  // «Линейка» - создаются один раз, но остаются скрытыми, пока не включат кнопку.
  map.addControl(new maplibregl.ScaleControl({ maxWidth: 110, unit: "metric" }), "bottom-left");
  ruler.setupLayers();
  routeEditor.setupLayers();

  pushDebug("map ready");
};

const tryInitMap = async () => {
  console.info("[MapView] tryInitMap start", !!map, mapEl.value);
  if (map) return;
  await nextTick();
  const el = resolveContainer();
  console.info("[MapView] resolved container", el);
  if (!el || !el.isConnected) {
    pushDebug("container missing, retrying...");
    scheduleInit();
    return;
  }
  pushDebug("initMap attempt", !!el);
  try {
    map = new maplibregl.Map({
      container: el,
      // Без style URL: каждый source/layer добавляется вручную в
      // setupMapLayers, карта стартует с намеренно пустого стиля, а не
      // подгружает чей-то хостируемый JSON. `glyphs` - единственное, что
      // нельзя добавить позже: это свойство уровня стиля, любой symbol-слой
      // без него падает при первой отрисовке.
      style: { version: 8, glyphs: glyphsUrl, sources: {}, layers: [] },
      center: MAP_DEFAULT_CENTER,
      // 14, а не 13 - тот же zoom, что и у maybeCenterInitially's jumpTo
      // (useNodeMarkers.ts), чтобы начальный вид полностью совпадал с тем,
      // куда карта асинхронно "допрыгивает" - иначе не прыгал бы центр, так
      // прыгал бы зум.
      zoom: 14,
      // minZoom привязан к границам, не к данным. z10 уже показывает чуть
      // больше всего разрешённого прямоугольника на десктопном вьюпорте;
      // ниже карта только добавляет степь, в которую нельзя попасть, вокруг
      // прямоугольника, из которого нельзя выйти.
      //
      // maxZoom 17 (было 15 у растровой подложки), потому что векторные
      // тайлы чисто overzoom-ятся - тайл z15 перерисовывается на 17, а не
      // размывается, как растянутый PNG, так что два лишних уровня
      // бесплатны и это как раз то, что важно при следовании треку пешком.
      minZoom: 10,
      maxZoom: 17,
      pitch: 0,
      maxPitch: 85,
      // В отличие от Leaflet, maxBounds у MapLibre реально ограничивает
      // панорамирование, так что старый обходной путь через ручной drag +
      // panInsideBounds больше не нужен.
      maxBounds: MAP_MAX_BOUNDS,
      attributionControl: { compact: true },
      // Кнопок +/- тоже нет (NavigationControl в MapLibre опционален),
      // зум щипком/колесом всё равно работает.
      // Добавляет настоящий заголовок Authorization (не утекающий в URL
      // ?token=) только к запросам тайлов POIS_ADMIN_SOURCE/BUILDINGS_ADMIN_SOURCE -
      // сопоставление по URL, так как у transformRequest нет понятия «какой
      // источник», только URL запроса. Остальные источники тайлов не
      // трогаются.
      transformRequest: (url: string) => {
        if (
          auth.accessToken &&
          (url.includes(`/${POIS_ADMIN_SOURCE}/`) || url.includes(`/${BUILDINGS_ADMIN_SOURCE}/`))
        ) {
          return { url, headers: { Authorization: `Bearer ${auth.accessToken}` } };
        }
        return { url };
      },
    });
    // Переносит настройку Leaflet wheelPxPerZoomLevel 60 -> 200: стандартная
    // чувствительность заставляет лёгкое движение на прецизионном трекпаде
    // Windows или мышке высокого разрешения прыгать сразу на несколько
    // уровней зума. У MapLibre тот же рычаг выражен как ставка, а не
    // пиксельный бюджет - это её дефолт (1/450), замедленный в ~3.3 раза.
    map.scrollZoom.setWheelZoomRate(1 / 1500);
    // Поворот двумя пальцами/правой кнопкой оставлен намеренно - это жест,
    // переводящий карту в режим 'manual' (см. обработчик rotatestart в
    // setupMapLayers).
    map.once("load", () => setupMapLayers(el));
    // preventDefault() обязателен - без него браузер не станет пытаться
    // восстановить контекст вообще (см. MDN webglcontextlost). Раньше этого
    // слушателя не было, и MapLibre продолжал рендерить в мёртвый контекст,
    // спамя "Could not compile fragment shader: null" на каждый кадр.
    map.getCanvas().addEventListener("webglcontextlost", (event) => {
      event.preventDefault();
      pushDebug("webgl context lost");
      mapContextLost.value = true;
    });
    // Не все браузеры/GPU когда-либо восстанавливают контекст сами - кнопка
    // в оверлее (recreateMap) даёт ручной путь, если это событие не придёт.
    map.getCanvas().addEventListener("webglcontextrestored", () => {
      pushDebug("webgl context restored, recreating map");
      recreateMap();
    });
  } catch (err: any) {
    pushDebug("MapLibre init failed", err?.message || String(err));
    scheduleInit(200);
  }
};

// Полный снос + пересоздание, а не попытка переиспользовать map после
// restore - в GL-ресурсах (source/layer/glyphs) после потери контекста
// нет гарантии консистентности, проще и надёжнее собрать всё заново
// (та же setupMapLayers, что и при обычном первом запуске).
const recreateMap = () => {
  mapReady = false;
  mapContextLost.value = false;
  try {
    map?.remove();
  }
  catch {
    // Контекст уже мёртв - remove() и так лучшее усилие, ошибку игнорируем.
  }
  map = null;
  tryInitMap();
};

let unsubscribeWeatherEvent: (() => void) | null = null;

onMounted(() => {
  console.info("[MapView] onMounted");
  pushDebug("MapView mounted, scheduling init");
  scheduleInit(0);
  unsubscribeWeatherEvent = onServerEvent("weather", () => {
    if (document.visibilityState === "visible") scheduleFetch(0);
  });
  loadRouteStyleConfig();
  document.addEventListener("visibilitychange", handleAppForeground);
  // Восстановление из bfcache (например, жест "назад" в Safari) не всегда
  // сопровождается visibilitychange - флаг persisted у pageshow покрывает и этот случай.
  window.addEventListener("pageshow", handleAppForeground);
  window.addEventListener("keydown", handleEditorEscape);
  window.addEventListener("keydown", handleRulerKeydown);
});

// Редактор всегда строит маршрут на чистой карте: трек открытой карточки на
// время редактирования убирается и возвращается при выходе - иначе чужая
// линия сливалась бы с рисуемой.
watch(
  () => props.routesVersion,
  () => {
    if (routeEditor.active.value) return;
    if (typeof props.selectedRouteId === "number") {
      loadSelectedRoute(props.selectedRouteId);
    }
  }
);

watch(
  () => props.selectedRouteId,
  (routeId) => {
    if (routeEditor.active.value) return;
    if (typeof routeId === "number") {
      loadSelectedRoute(routeId);
    } else {
      clearCurrentRoute();
    }
  }
);

watch(
  () => routeEditor.active.value,
  (editing) => {
    if (editing) {
      clearCurrentRoute();
    } else if (typeof props.selectedRouteId === "number") {
      loadSelectedRoute(props.selectedRouteId);
    }
  }
);

watch(
  () => props.cursorDistanceKm,
  (km) => syncCursorFromProp(km)
);

watch(mapRotationMode, () => gps.refreshUserHeadingVisual());

// Закрыли «жучок» - убираем и оверлей покрытия DEM.
watch(debugPanelOpen, (open) => {
  if (!open && demCoverageOn.value) {
    demCoverageOn.value = false;
    setDemCoverageVisible(false);
  }
});

// Переключатель языка меняет только ref locale у vue-i18n; подписи базовой
// карты - не Vue-рендеренный текст, никто больше его не перечитает. Это вся
// живая половина фичи - без неё карта держала бы язык постройки до
// перезагрузки. Безопасно до существования карты: applyLabelLocale no-op,
// пока не mapReady.
watch(locale, () => applyLabelLocale());

onBeforeUnmount(() => {
  unsubscribeWeatherEvent?.();
  document.removeEventListener("visibilitychange", handleAppForeground);
  window.removeEventListener("pageshow", handleAppForeground);
  window.removeEventListener("keydown", handleEditorEscape);
  window.removeEventListener("keydown", handleRulerKeydown);
  ruler.destroy();
  routeEditor.destroy();
  pointTool.destroy();
  removeCursorMarker();
  resizeObserver?.disconnect();
  resizeObserver = null;
  stopNodesPoll?.();
  stopNodesPoll = null;
  if (fetchTimeout) {
    clearTimeout(fetchTimeout);
  }
  if (initTimeout) {
    clearTimeout(initTimeout);
  }
  gps.destroy();
  routeDrawing.resetForUnmount();
  nodeMarkers.forEach((marker) => marker.remove());
  nodeMarkers.clear();
  poiLayers.destroy();
  mapReady = false;
  // Уничтожает GL-контекст, воркеры и все слушатели, зарегистрированные на
  // самой карте (слушатель на контейнере выше уходит вместе с элементом).
  map?.remove();
  map = null;
});

// Редактор маршрутов управляется отсюда (нужен доступ к карте/ctx), но File/
// View-меню теперь живут в App.vue, замещая обычную навигацию (левая
// панель/нижняя мобильная панель) - App.vue получает ref на этот компонент
// и читает/дёргает всё через этот defineExpose, не дублируя состояние.
defineExpose({
  routeEditor,
  handleEditorNew,
  handleEditorImportOpen,
  openSaveDialog,
  handleEditorExport,
  handleEditorClose,
  is3D,
  toggle3D,
});

console.info("[MapView] setup end");
</script>
